// src/utils/generateId.js
//
// Generates a unique human-readable ID in the format:
//   USR + DDMMYYYY + NNN   →  e.g.  USR19042026001
//   VND + DDMMYYYY + NNN   →  e.g.  VND19042026001
//
// Primary path : Firestore transaction on "counters" collection (atomic,
//                daily-reset, shared across all devices).
// Fallback path: If Firestore is unreachable OR the user doesn't have
//                permission on the "counters" collection (e.g. the Firestore
//                security rules haven't been updated yet), we generate a
//                time-based ID in the browser so registration NEVER fails.

import { db } from "../firebase";
import { doc, runTransaction } from "firebase/firestore";

/** Returns today's date as DDMMYYYY (e.g. 19042026) in the user's locale. */
function getTodayDDMMYYYY() {
  const now = new Date();
  const dd   = String(now.getDate()).padStart(2, "0");
  const mm   = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = String(now.getFullYear());
  return `${dd}${mm}${yyyy}`;
}

/**
 * Local-only fallback ID — used when Firestore counter can't be written.
 * Uses seconds-since-midnight padded to 3 digits (then wraps to 000-999),
 * which keeps the format identical and still mostly unique within a day.
 */
function localFallbackId(role) {
  const today = getTodayDDMMYYYY();
  const now = new Date();
  const secs = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const seq  = String(secs % 1000).padStart(3, "0");
  return `${role}${today}${seq}`;
}

/**
 * Generates a unique ID.
 *
 * @param {"USR" | "VND"} role - Prefix (defaults to USR)
 * @returns {Promise<string>}  - e.g. "USR19042026001"
 */
export async function generateId(role = "USR") {
  const today     = getTodayDDMMYYYY();
  const counterId = `${role}_${today}`;
  const counterRef = doc(db, "counters", counterId);

  try {
    return await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(counterRef);
      const current = snapshot.exists() ? snapshot.data().count : 0;
      const next    = current + 1;

      transaction.set(counterRef, {
        count: next,
        role,
        date: today,
        updatedAt: new Date()
      });

      return `${role}${today}${String(next).padStart(3, "0")}`;
    });
  } catch (err) {
    // ✅ Never break registration — log and fall back to a local ID.
    console.warn(
      "[generateId] Firestore counter failed, falling back to local ID.",
      err?.message || err
    );
    return localFallbackId(role);
  }
}